import querystring from 'query-string'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { axiosFetch, isAxiosCancellation, isAxiosResponseError } from '@halo/_utils/axios'
import { Singleton, Container } from 'typescript-ioc/es5'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainDto } from '@halo/dtos/domain.dto'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import aqe from '@pushly/aqe'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { ApiKeyDto } from '@halo/dtos/user-with-key.dto'
import {
    BillingDataResponse,
    BillingRecordResponse,
    CreateBillingRecordRequest,
    CreateBillingRecordResponse,
    GetBillableAccountResponse,
    SubscriberHistoryResponse,
    UpdateBillingDataRequest,
} from '@pushly/models/lib/structs/billing'

@Singleton
export class AccountService extends AbstractRequestService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        super()
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public async fetchAll(opts: IApiCallOptions = {}): Promise<IApiResponse<any[]>> {
        let ok = false
        let accounts: any = []
        let meta: any

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query ?? {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )
            const { data, ...resMeta } = res.data

            ok = true
            accounts = data
            meta = resMeta
        } catch (error) {}

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: accounts, meta }
    }

    public async fetchBillable(
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<GetBillableAccountResponse[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(HttpMethod.GET, `${aqe.defaults.publicApiDomain}/v3/accounts/billable`, {
                query: opts,
                cancellationKey,
                responseType: [GetBillableAccountResponse],
            })
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async fetchById(
        accountId: number,
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<AccountDto> {
        let dto: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}?${options}`

        try {
            const req = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                cancellationKey,
            )

            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            dto = AccountDto.fromApiResponse(req.data.data)
        } catch (error) {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        return dto!
    }

    public async create(
        createDto: Partial<AccountDto>,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<AccountDto>> {
        let ok = false
        let org: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/accounts`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                cancellationKey,
            )

            ok = true
            org = AccountDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Organization '${org.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: org }
    }

    public async patch(
        accountId: number,
        updateDto: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<AccountDto>> {
        let ok = false
        let org: AccountDto

        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        // USING PUB API Endpoint to enable cascading functionality
        // Contains all the same functionality as previously contained in the INT_API
        const serviceURL = `${aqe.defaults.publicApiDomain}/accounts/${accountId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: updateDto,
                },
                cancellationKey,
            )

            ok = true
            org = AccountDto.fromApiResponse(req.data.data)
            simpleNotification('success', `Organization '${org.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: org }
    }

    public async patchBillingData(
        accountId: number,
        billingDto: UpdateBillingDataRequest,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<BillingDataResponse>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(
                HttpMethod.PATCH,
                `${aqe.defaults.publicApiDomain}/v1/accounts/${accountId}/billing-data`,
                {
                    data: billingDto,
                    cancellationKey,
                    responseType: BillingDataResponse,
                },
            )
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async fetchBillingRecordsByAccountId(
        accountId: number,
        opts: any = {},
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<BillingRecordResponse[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(
                HttpMethod.GET,
                `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}/billing-records`,
                {
                    query: opts,
                    cancellationKey,
                    responseType: [BillingRecordResponse],
                },
            )
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async fetchBillingRecordLogs(
        accountId: number,
        recordId: number,
        opts: any = {},
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<any[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            // Always include remote events for internal tools (halocline)
            const queryOpts = { ...opts, include_remote_events: 'true' }
            return await this.request(
                HttpMethod.GET,
                `${aqe.defaults.publicApiDomain}/v1/accounts/${accountId}/billing-records/${recordId}/logs`,
                {
                    query: queryOpts,
                    cancellationKey,
                },
            )
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async createBillingRecordForAccountId(
        accountId: number,
        recordDto: CreateBillingRecordRequest,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<CreateBillingRecordResponse>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(
                HttpMethod.POST,
                `${aqe.defaults.publicApiDomain}/v1/accounts/${accountId}/billing-records`,
                {
                    data: recordDto,
                    cancellationKey,
                    responseType: CreateBillingRecordResponse,
                },
            )
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async confirmBillingRecord(
        tokenizedEndpoint: string,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<BillingRecordResponse>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(HttpMethod.POST, `${aqe.defaults.publicApiDomain}${tokenizedEndpoint}`, {
                data: {},
                cancellationKey,
                responseType: BillingRecordResponse,
            })
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async fetchDomains(accountId: number, opts: IApiCallOptions = {}): Promise<IApiResponse<DomainDto[]>> {
        let ok = false
        let dtos: DomainDto[] = []
        let meta: any

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/accounts/${accountId}/domains?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            const { data, ...resMeta } = res.data

            ok = true
            dtos = data.map(DomainDto.fromApiResponse)
            meta = resMeta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: dtos, meta }
    }

    public async fetchSubscriberHistoryByAccountId(
        accountId: number,
        opts?: any,
        showLoadingScreen: boolean = true,
        cancellationKey?: string,
    ): Promise<IApiResponse<SubscriberHistoryResponse[]>> {
        if (showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return await this.request(
                HttpMethod.GET,
                `${aqe.defaults.publicApiDomain}/v1/accounts/${accountId}/subscriber-history`,
                {
                    query: opts,
                    cancellationKey,
                    responseType: [SubscriberHistoryResponse],
                },
            )
        } finally {
            if (showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async fetchInternalApiUsers(opts: IApiCallOptions = {}): Promise<IApiResponse<ApiKeyDto[]>> {
        let response: IApiResponse<ApiKeyDto[]> = {
            ok: false,
            data: [],
        }

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys`

        try {
            const req = await axiosFetch('get', {
                url: serviceUrl,
            })

            if (opts.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }

            const { data: apiUser, ...reqMeta } = req.data

            response.ok = true
            response.data = apiUser.data.map(ApiKeyDto.fromApiResponse)
            response.meta = reqMeta
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }

    public async regenerateInternalApiKey(
        apiKey: string,
        opts?: IApiCallOptions,
    ): Promise<IApiResponse<ApiKeyDto | null>> {
        let response: IApiResponse<ApiKeyDto | null> = {
            ok: false,
            data: null,
        }

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceUrl = `${aqe.defaults.publicApiDomain}/system/api-keys/${apiKey}/regenerate`

        try {
            const req = await axiosFetch('post', {
                url: serviceUrl,
            })

            const { data: apiUser, ...reqMeta } = req.data
            response.ok = true
            response.data = apiUser
            response.meta = reqMeta
        } catch (error) {
            response.error = error
            response.cancelled = isAxiosCancellation(error)

            if (!response.cancelled) {
                simpleNotification('error', error.response?.data?.message)
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return response
    }
}
